import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { CheckCircle2, Loader2, X } from "lucide-react";
import { useStore } from "store";
import { SnackBarContext } from "components/snack-bar";
import { sendClientVerificationByEmail, isClientEmailVerified } from "graphQL/mutations/user";

interface ConfirmEmailModalProps {
  open: boolean;
  /** Dismiss (X / backdrop / Esc) — no submission, user stays on Step 3. */
  onClose: () => void;
  /** Fired once the email is verified — the parent then opens the order drawer. */
  onVerified: () => void;
}

const POLL_INTERVAL_MS = 5000;
const SUCCESS_AUTOCLOSE_MS = 2000;
// Throttle explicit resends to the backend rate-limit window. Shared localStorage
// key keeps the cooldown in sync with the header VerificationAlert resend timer.
const RESEND_COOLDOWN_MS = 120000;
const RESEND_TIMER_KEY = "resend_timer";
const RATE_LIMIT_ERROR = "for the moment you are limited to this action";

type SendResult = "success" | "rate-limited" | "error";

// Cooldown persistence is best-effort: blocked storage (SecurityError in some
// embedded/incognito contexts) falls back to in-memory state so the gate still works.
let memoryResendExpiry = 0;
const readResendExpiry = (): number => {
  try {
    return Number(localStorage.getItem(RESEND_TIMER_KEY)) || 0;
  } catch {
    return memoryResendExpiry;
  }
};
const writeResendExpiry = (expiry: number): void => {
  memoryResendExpiry = expiry;
  try {
    localStorage.setItem(RESEND_TIMER_KEY, String(expiry));
  } catch {
    // storage unavailable — in-memory fallback keeps the cooldown for this session
  }
};

const formatCooldown = (seconds: number) => {
  const minutes = Math.floor(seconds / 60);
  const remaining = seconds % 60;
  return `${minutes}:${remaining < 10 ? `0${remaining}` : remaining}`;
};

// "Confirm your email" gate shown on Step 3 before the order drawer opens. The
// verification email is auto-sent on open; we poll the account verification status
// (the user confirms via the email link) and, once verified, auto-advance to the
// drawer. Reuses the same account-email-verification flow as the header alert.
export const ConfirmEmailModal: React.FC<ConfirmEmailModalProps> = ({ open, onClose, onVerified }) => {
  const runSnackBar = useContext(SnackBarContext);
  const { updateUser } = useStore();
  const [verified, setVerified] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const sentRef = useRef(false);

  const startCooldown = useCallback(() => {
    writeResendExpiry(Date.now() + RESEND_COOLDOWN_MS);
    setCooldown(Math.floor(RESEND_COOLDOWN_MS / 1000));
  }, []);

  // Sends the verification email. When `notify` is set (explicit Resend click) the
  // result is surfaced via the snackbar; the auto-send on open stays silent.
  const sendEmail = useCallback(
    async (notify: boolean): Promise<SendResult> => {
      try {
        const response = await sendClientVerificationByEmail();
        const {
          data: { sendClientVerificationEmail },
        } = await (response as Response).json();
        if (sendClientVerificationEmail?.success) {
          if (notify) {
            runSnackBar({
              type: "success",
              vertical: "top",
              msg: "Email sent successfully. Please check your mailbox.",
            });
          }
          return "success";
        }
        const rateLimited = sendClientVerificationEmail?.error === RATE_LIMIT_ERROR;
        if (notify) {
          const msg = rateLimited
            ? "Wait at least 2 minutes. You have already sent a request"
            : "Email could not be sent. Please try again later.";
          runSnackBar({ type: "error", vertical: "top", msg });
        }
        return rateLimited ? "rate-limited" : "error";
      } catch (error) {
        if (notify) {
          runSnackBar({ type: "error", vertical: "top", msg: "Email could not be sent. Please try again later." });
        }
        return "error";
      }
    },
    [runSnackBar]
  );

  // Auto-send on open + poll for confirmation.
  useEffect(() => {
    if (!open) {
      sentRef.current = false;
      setVerified(false);
      setResending(false);
      setCooldown(0);
      return undefined;
    }
    // Restore a cooldown still running from a prior resend (here or the header alert).
    const expireTime = readResendExpiry();
    const remaining = expireTime ? Math.floor((expireTime - Date.now()) / 1000) : 0;
    if (remaining > 0) {
      setCooldown(remaining);
      // A send is still within the backend window — skip the auto-send so we don't
      // fire a request that would just be rate-limited.
      sentRef.current = true;
    }
    if (!sentRef.current) {
      sentRef.current = true;
      void sendEmail(false);
    }
    let cancelled = false;
    const checkStatus = async () => {
      try {
        const response = await isClientEmailVerified();
        const {
          data: { getClientEmailVerified },
        } = await (response as Response).json();
        if (!cancelled && getClientEmailVerified?.success) {
          setVerified(true);
          updateUser({ isVerified: true });
        }
      } catch (error) {
        // keep polling
      }
    };
    const interval = setInterval(checkStatus, POLL_INTERVAL_MS);
    void checkStatus();
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [open, sendEmail, updateUser]);

  // Tick the resend cooldown down to zero, then re-enable the button.
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setInterval(() => setCooldown((prev) => (prev <= 1 ? 0 : prev - 1)), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  // Once verified, briefly show the success state, then continue to the drawer.
  // Gated on `open` so dismissing the modal during the success window (Esc /
  // backdrop / X) cancels the auto-advance instead of opening the drawer anyway.
  useEffect(() => {
    if (!open || !verified) return undefined;
    const timer = setTimeout(() => onVerified(), SUCCESS_AUTOCLOSE_MS);
    return () => clearTimeout(timer);
  }, [open, verified, onVerified]);

  // Esc to dismiss.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleResend = async () => {
    if (resending || verified || cooldown > 0) return;
    setResending(true);
    try {
      const result = await sendEmail(true);
      // Both a successful send and a backend rate-limit mean another request must
      // wait — disable the button for the cooldown window either way.
      if (result === "success" || result === "rate-limited") startCooldown();
    } finally {
      setResending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1300] flex items-center justify-center"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      onMouseDown={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-email-title"
        className="relative flex w-[440px] max-w-[90vw] flex-col gap-6 rounded-xl border border-solid border-border bg-card p-6 shadow-[0px_10px_15px_rgba(0,0,0,0.15)]"
        onMouseDown={(event) => event.stopPropagation()}
        data-cypress-id="confirmEmailModal"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 absolute right-[7px] top-[7px] p-2"
          data-cypress-id="confirmEmailClose"
          data-cy="admConfirmEmailCloseButton"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="flex flex-col gap-2">
          <h2 id="confirm-email-title" className="text-lg font-medium leading-7 text-foreground">
            Confirm your email
          </h2>
          <p className="text-sm leading-5 text-muted-foreground">
            For security reasons, we need you to confirm your email before submitting. Don&apos;t forget to look in your
            spam folder!
          </p>
        </div>

        <div className="flex min-h-9 items-center justify-between gap-3">
          {verified ? (
            <div className="flex min-w-0 items-center gap-2">
              <CheckCircle2 className="h-4 w-4 shrink-0 text-success-foreground" />
              <span className="truncate text-sm leading-5 text-success-foreground">Email verified successfully!</span>
            </div>
          ) : (
            <div className="flex min-w-0 items-center gap-2">
              <Loader2 className="h-4 w-4 shrink-0 animate-spin text-[#ef2b55]" />
              <span className="truncate text-sm leading-5 text-foreground">Awaiting verification</span>
            </div>
          )}

          <button
            type="button"
            onClick={handleResend}
            disabled={resending || verified || cooldown > 0}
            className="inline-flex h-9 shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg bg-[#ef2b55] px-4 text-sm font-medium leading-5 text-white disabled:cursor-not-allowed disabled:opacity-50"
            data-cypress-id="confirmEmailResend"
            data-cy="admConfirmEmailResendButton"
          >
            Resend verification email
          </button>
        </div>
      </div>
    </div>
  );
};
